import Dropdown from "~/components/shared/Dropdown";
import Icon from "~/components/shared/Icon";
import { Build } from "~/components/build/Show/lib/types";
import { twMerge } from "tailwind-merge";

interface Props {
  build: Build;
  className?: string;
}

const DropdownItem = ({ children, ...props }: React.HTMLProps<HTMLAnchorElement>) => (
  <a
    {...props}
    rel="nofollow"
    className="mx-1.5 flex items-center gap-1 rounded-md px-2 py-2 font-medium text-inherit hover:bg-purple-100 hover:text-purple-600 hover:no-underline"
  >
    {children}
  </a>
);

export function RebuildButton({ build, className }: Props) {
  // Rebuild is independent of the current build's state — if the user has
  // permission to rebuild a project that allows rebuilds, they can do so
  // regardless of whether the current build is running, canceling, or
  // finished. The backend `Build::Rebuilder` has no running-state restriction.
  if (!build.project.archived) {
    if (build.permissions.rebuild.allowed && build.project.allowRebuilds) {
      if (Features.BuildSidebar && build.state === "passed") {
        return (
          <div className="shrink-0">
            <a
              href={build.newPath}
              className={twMerge(
                className,
                "btn build-rebuild-button btn-flex rounded-r-none text-center",
              )}
              data-turbo={true}
              data-turbo-frame="modal_dialog"
            >
              <Icon
                icon="heroicons/outline/plus"
                style={{ width: 14, height: 14 }}
                className="text-charcoal-700 mr-1"
              />
              <span>New build</span>
            </a>

            <Dropdown width={200} className="inline-block">
              <button
                className={twMerge(
                  className,
                  "btn-flex cursor-pointer select-none rounded-l-none border-l-0",
                )}
                title="Rebuild options"
              >
                <Icon icon="down-triangle" style={{ width: 7, height: 7 }} />
              </button>

              <DropdownItem href={build.rebuildPath} data-method="post">
                <Icon icon="custom/outline/16x/arrows-refresh" style={{ width: 16, height: 16 }} />
                <span>Rebuild</span>
              </DropdownItem>

              <DropdownItem href={build.rebuildPath} data-method="post">
                <Icon
                  icon="custom/outline/git-commit-horizontal"
                  style={{ width: 16, height: 16 }}
                />
                <span>Rebuild this commit</span>
              </DropdownItem>

              <DropdownItem href={build.rebuildBranchPath} data-method="post">
                <Icon icon="custom/outline/git-branch" style={{ width: 16, height: 16 }} />
                <span>Rebuild this branch</span>
              </DropdownItem>
            </Dropdown>
          </div>
        );
      }
      return (
        <div className="shrink-0">
          <a
            className={twMerge(
              className,
              "btn build-rebuild-button btn-flex rounded-r-none text-center",
            )}
            data-method="post"
            href={build.rebuildPath}
            rel="nofollow"
          >
            <Icon
              icon="custom/outline/16x/arrows-refresh"
              style={{ width: 16, height: 16 }}
              className="text-charcoal-700 mr-1"
            />

            <span>Rebuild</span>
          </a>

          <Dropdown width={200} className="inline-block">
            <button
              className={twMerge(
                className,
                "btn btn-flex cursor-pointer select-none rounded-l-none border-l-0",
              )}
              title="Rebuild options"
            >
              <Icon icon="down-triangle" style={{ width: 7, height: 7 }} />
            </button>

            <DropdownItem href={build.rebuildPath} data-method="post">
              <Icon icon="custom/outline/git-commit-horizontal" style={{ width: 16, height: 16 }} />
              <span>Rebuild this commit</span>
            </DropdownItem>

            <DropdownItem href={build.rebuildBranchPath} data-method="post">
              <Icon icon="custom/outline/git-branch" style={{ width: 16, height: 16 }} />
              <span>Rebuild this branch</span>
            </DropdownItem>

            <DropdownItem href={build.newPath} data-turbo={true} data-turbo-frame="modal_dialog">
              <Icon icon="heroicons/outline/plus" style={{ width: 14, height: 14 }} />
              <span>New build</span>
            </DropdownItem>
          </Dropdown>
        </div>
      );
    } else if (build.permissions.rebuild.reason !== "anonymous") {
      return (
        <span
          className={twMerge(
            "btn btn-flex btn-default build-rebuild-button btn--disabled text-center",
            className,
          )}
          aria-label={build.permissions.rebuild.message}
          data-title-tooltip={build.permissions.rebuild.message}
        >
          <Icon
            icon="custom/outline/16x/arrows-refresh"
            style={{ width: 16, height: 16 }}
            className="mr-1"
          />
          <span>Rebuild</span>
        </span>
      );
    }
  }

  return null;
}
